import { useEffect, useState } from "react";
import type { ErrorEntry } from "@/lib/types";

interface Props {
  errors: Array<ErrorEntry & { id: string }>;
}

const AUTO_DISMISS_MS = 5_000;

const codeLabel: Record<string, string> = {
  product_max_quantity: "Limite atteinte sur un produit",
  product_round_quantity: "Limite par tournée atteinte",
  category_max_quantity: "Limite catégorie atteinte",
  global_max_quantity: "Quantité totale dépassée",
  global_max_quantity_per_menu: "Limite menu atteinte",
  product_unavailable: "Un produit du panier n'est plus disponible à cette heure",
  max_orders: "Limite de tournées atteinte",
  ordering_suspended: "Commandes suspendues",
  ordering_disabled: "Commande indisponible sur ce canal",
  waiter_password_invalid: "Code serveur incorrect",
  cooldown_active: "Trop tôt pour relancer la cuisine",
  pending_already_in_progress: "Un envoi est déjà en cours",
};

export const ErrorToast = ({ errors }: Props): JSX.Element => {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    const timers = errors.map((e) => {
      if (dismissed.has(e.id)) return 0;
      return window.setTimeout(() => {
        setDismissed((prev) => new Set(prev).add(e.id));
      }, AUTO_DISMISS_MS);
    });
    return () => timers.forEach((t) => t && clearTimeout(t));
  }, [errors, dismissed]);

  const visible = errors.filter((e) => !dismissed.has(e.id)).slice(-3);
  if (!visible.length) return <></>;

  return (
    <div className="fixed bottom-32 inset-x-4 z-40 space-y-2 max-w-md mx-auto pointer-events-none">
      {visible.map((e) => (
        <div
          key={e.id}
          className="bg-primary-deep text-white rounded-card px-4 py-3 shadow-warm-lg animate-slide-up pointer-events-auto flex items-start gap-3"
          role="alert"
        >
          <span aria-hidden="true" className="text-lg">
            ⚠
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">
              {codeLabel[e.code] ?? e.message ?? "Une erreur est survenue"}
            </p>
            {e.message && codeLabel[e.code] && (
              <p className="text-white/75 text-xs mt-0.5 line-clamp-2">{e.message}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setDismissed((prev) => new Set(prev).add(e.id))}
            className="text-white/75 text-xs"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
};
